import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  BarChart3,
  Clock,
  Search,
  Eye,
  Edit,
  Copy,
  Trash2,
  Share2,
  Globe,
  GlobeLock,
  Loader2,
  MoreVertical,
} from 'lucide-react';
import { useForms } from '../../hooks/useForms';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../Button';
import { Input } from '../ui/Input';
import { StatsCard } from '../ui/StatsCard';
import { EmptyState } from '../ui/EmptyState';
import { Modal } from '../ui/Modal';
import { Reveal } from '../Reveal';
import type { Form, FormsFilter } from '../../types/forms';
import { createDefaultField } from '../../lib/fieldPalette';
import { usePlan } from '../../hooks/usePlan';
import { useUpgradeGate } from '../billing/UpgradeGate';

export const FormsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deleteModal, setDeleteModal] = useState<Form | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const closeDropdown = () => {
    setActiveDropdown(null);
    setMenuPosition(null);
  };

  const toggleDropdown = (formId: string) => {
    if (activeDropdown === formId) {
      closeDropdown();
      return;
    }
    const trigger = triggerRefs.current[formId];
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const MENU_WIDTH = 192;
    const GUTTER = 8;
    const left = Math.max(GUTTER, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - GUTTER));
    setMenuPosition({ top: rect.bottom + GUTTER, left });
    setActiveDropdown(formId);
  };

  useEffect(() => {
    if (!activeDropdown) return;
    const onDismiss = () => closeDropdown();
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener('keydown', onKey);
    };
  }, [activeDropdown]);

  const filter: FormsFilter = {
    search: search || undefined,
    status: statusFilter,
    isTemplate: false,
    sortBy: 'updated_at',
    sortOrder: 'desc',
  };

  const { forms, loading, error, createForm, deleteForm, duplicateForm, togglePublish } = useForms(filter);

  // Estatísticas
  const totalForms = forms.length;
  const publishedForms = forms.filter(f => f.is_published).length;
  const totalResponses = forms.reduce((acc, f) => acc + (f.response_count || 0), 0);

  const { effectiveTier } = usePlan();
  const { ensureLimit } = useUpgradeGate();

  const handleCreateForm = async () => {
    const nextTier: 'pro' | 'business' = effectiveTier === 'pro' ? 'business' : 'pro';
    const reason =
      effectiveTier === 'pro'
        ? 'Você atingiu o limite de formulários do plano Pro. Faça upgrade para Business para criar formulários ilimitados.'
        : 'Você atingiu o limite de formulários do plano Free. Faça upgrade para Pro para criar até 25 formulários.';

    if (
      !ensureLimit('forms_max', forms.length, reason, {
        tierRequired: nextTier,
      })
    ) {
      return;
    }

    const welcomeField = createDefaultField('welcome_screen');
    welcomeField.label = 'Bem-vindo';
    welcomeField.helpText = 'Preencha o formulário abaixo';

    const { form } = await createForm({
      title: 'Novo Formulário',
      description: '',
      fields: [welcomeField],
    });

    if (form) {
      navigate(`/dashboard/forms/${form.id}/edit`);
    }
  };

  const handleDeleteForm = async () => {
    if (!deleteModal) return;

    setIsDeleting(true);
    await deleteForm(deleteModal.id);
    setIsDeleting(false);
    setDeleteModal(null);
  };

  const handleDuplicateForm = async (form: Form) => {
    const { form: newForm } = await duplicateForm(form.id);
    if (newForm) {
      navigate(`/dashboard/forms/${newForm.id}/edit`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <Reveal variant="fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Olá, {user?.full_name?.split(' ')[0] || 'usuário'}
            </h1>
            <p className="text-neutral-400 mt-1">
              Gerencie seus formulários e veja as respostas
            </p>
          </div>

          <Button variant="primary" onClick={handleCreateForm}>
            <Plus className="w-5 h-5 mr-2" />
            Novo formulário
          </Button>
        </div>
      </Reveal>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatsCard
          icon={<FileText className="w-6 h-6" />}
          label="Total de formulários"
          value={totalForms}
          delay={100}
        />
        <StatsCard
          icon={<Globe className="w-6 h-6" />}
          label="Publicados"
          value={publishedForms}
          delay={200}
        />
        <StatsCard
          icon={<BarChart3 className="w-6 h-6" />}
          label="Respostas recebidas"
          value={totalResponses}
          delay={300}
        />
      </div>

      {/* Filters */}
      <Reveal variant="fade-up" delay={400}>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Buscar formulários..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-5 h-5" />}
            />
          </div>

          <div className="flex gap-2">
            {(['all', 'published', 'draft'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium transition-all
                  ${statusFilter === status
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                  }
                `}
              >
                {status === 'all' ? 'Todos' : status === 'published' ? 'Publicados' : 'Rascunhos'}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Forms List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-20">
          <p className="text-red-400">{error}</p>
        </div>
      ) : forms.length === 0 ? (
        <EmptyState
          icon={<FileText className="w-10 h-10" />}
          title="Nenhum formulário encontrado"
          description={search ? 'Tente ajustar sua busca' : 'Crie seu primeiro formulário para começar a coletar respostas'}
          actionLabel="Criar formulário"
          onAction={handleCreateForm}
        />
      ) : (
        <div className="space-y-4">
          {forms.map((form, index) => (
            <Reveal key={form.id} variant="fade-up" delay={500 + index * 50}>
              <div className="liquid-glass rounded-2xl p-6 hover:border-primary-500/30 transition-all group">
                <div className="flex items-start justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/forms/${form.id}/edit`)}
                        className="text-lg font-semibold text-white truncate text-left hover:text-primary-400 transition-colors focus:outline-none focus-visible:underline min-w-0"
                        title="Editar formulário"
                      >
                        {form.title}
                      </button>
                      {form.is_published ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          <Globe className="w-3 h-3" />
                          Publicado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-neutral-700 text-neutral-400">
                          <GlobeLock className="w-3 h-3" />
                          Rascunho
                        </span>
                      )}
                    </div>

                    {form.description && (
                      <p className="text-neutral-400 text-sm mb-3 line-clamp-1">
                        {form.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span className="flex items-center gap-1">
                        <BarChart3 className="w-4 h-4" />
                        {form.response_count || 0} respostas
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(form.updated_at)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center flex-shrink-0">
                    <button
                      ref={(el) => { triggerRefs.current[form.id] = el; }}
                      onClick={() => toggleDropdown(form.id)}
                      className="p-3 sm:p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                      title="Mais opções"
                      aria-label="Mais opções"
                      aria-haspopup="menu"
                      aria-expanded={activeDropdown === form.id}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      {/* Dropdown Menu (portal escapes liquid-glass stacking context) */}
      {activeDropdown && menuPosition && (() => {
        const form = forms.find(f => f.id === activeDropdown);
        if (!form) return null;
        return createPortal(
          <>
            <div
              className="fixed inset-0 z-[60]"
              onClick={closeDropdown}
            />
            <div
              role="menu"
              className="fixed w-48 bg-neutral-800 border border-neutral-700 rounded-xl shadow-xl z-[70] py-2"
              style={{ top: menuPosition.top, left: menuPosition.left }}
            >
              <button
                role="menuitem"
                onClick={() => {
                  navigate(`/dashboard/forms/${form.id}/responses`);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Eye className="w-4 h-4" />
                Ver respostas
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  navigate(`/dashboard/forms/${form.id}/edit`);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  navigate(`/dashboard/forms/${form.id}/share`);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Share2 className="w-4 h-4" />
                Compartilhar
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  handleDuplicateForm(form);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-neutral-300 hover:bg-neutral-700/50"
              >
                <Copy className="w-4 h-4" />
                Duplicar
              </button>
              <div className="my-1 border-t border-neutral-700/50" />
              <button
                role="menuitem"
                onClick={() => {
                  setDeleteModal(form);
                  closeDropdown();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          </>,
          document.body
        );
      })()}

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Excluir formulário"
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-neutral-400">
            Tem certeza que deseja excluir <strong className="text-white">"{deleteModal?.title}"</strong>?
            Esta ação não pode ser desfeita e todas as respostas serão perdidas.
          </p>

          <div className="flex gap-4">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setDeleteModal(null)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <button
              onClick={handleDeleteForm}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Excluir
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
